<script setup lang="ts">
import type { APISearchLocationItem } from '../../types'
import type { SearchQuery } from './YscSearchWidget.vue'
import { computed, useNuxtApp } from '#imports'

interface Props {
  localDestination?: string
  results?: APISearchLocationItem[]
  storedLastQueries?: SearchQuery[]
}

const props = withDefaults(defineProps<Props>(), {
  localDestination: undefined,
  results: () => [],
  storedLastQueries: () => [],
})

const emits = defineEmits<{
  'destination-click': [APISearchLocationItem]
  'around-me': []
}>()

const destinationLabels = (result: APISearchLocationItem) => {
  if (!result.address) {
    return { label: result.label }
  }
  //  For example for Miami the data is in county and city is null
  //  For instance Guadeloupe is returned as state
  return {
    label: result.address.city ?? result.address.county ?? result.address.state,
    subLabel: [result.address.postalCode, result.address.state, result.address.countryName].filter(Boolean).join(', '),
  }
}

const shouldDisplayStoredResults = computed(() => {
  return !props.localDestination && props.storedLastQueries?.length
})

const { $i18n: { locale } } = useNuxtApp()
</script>

<template>
  <div class="mt-4 md:mt-0 px-4 flex flex-col md:flex-row">
    <div
      v-if="shouldDisplayStoredResults"
      class="grow"
    >
      <p
        class="font-semibold my-2 md:ml-2"
      >
        {{ $t('data.search.recent') }}
      </p>
      <YscSearchNavigableList
        v-slot="{ item }"
        :list="storedLastQueries"
        list-key="locationId"
        @selected="emits('destination-click', $event)"
      >
        <div class="text-black">
          {{ destinationLabels(item).label }}
          <span v-if="item.date_from && item.date_to">
            — {{ $d(new Date(item.date_from), 'M.short D.numeric', locale) }} - {{ $d(new Date(item.date_to), 'Y.numeric M.short D.numeric', locale) }}
          </span>
        </div>
        <div
          v-if="destinationLabels(item).subLabel"
          class="text-gray-400"
        >
          {{ destinationLabels(item).subLabel }}
        </div>
      </YscSearchNavigableList>
    </div>
    <YscSearchNavigableList
      v-else-if="results && results.length"
      v-slot="{ item }"
      :list="results"
      list-key="locationId"
      class="grow"
      @selected="emits('destination-click', $event)"
    >
      <div class="text-black">
        {{ destinationLabels(item).label }}
      </div>
      <div
        v-if="destinationLabels(item).subLabel"
        class="text-gray-400"
      >
        {{ destinationLabels(item).subLabel }}
      </div>
    </YscSearchNavigableList>
    <div class="grow">
      <p
        class="font-semibold my-2 md:ml-2"
      >
        {{ $t('data.search.close_by') }}
      </p>
      <button
        class="flex items-center md:p-2 w-full hover:bg-gray-50"
        @click.prevent.stop="emits('around-me')"
      >
        <div class="p-2 bg-gray-100 rounded mr-2">
          <YscIconsGeolocalization class="w-6 h-6 text-gray-500" />
        </div>
        <span class="text-pink-500">{{ $t('data.search.search_around_me') }}</span>
      </button>
    </div>
  </div>
</template>
